import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Lock, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100 p-6">
        <div className="w-full max-w-md bg-white border-2 border-red-600 p-8 text-center">
          <div className="font-display text-xl font-black uppercase mb-2">Invalid link</div>
          <p className="text-sm text-zinc-600 mb-6">This reset link is missing its token. Please request a new one.</p>
          <Link to="/forgot-password" className="kr-btn-primary py-2.5 px-6 text-sm inline-block">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-100 p-6">
      <div className="w-full max-w-md bg-white border-2 border-zinc-900 p-8" data-testid="reset-password-page">
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-orange-600"
          >
            <ArrowLeft size={12} weight="bold" /> Back to login
          </Link>
        </div>

        {done ? (
          <div className="text-center" data-testid="reset-success-state">
            <div className="w-12 h-12 bg-green-600 text-white flex items-center justify-center mb-5 mx-auto">
              <CheckCircle size={26} weight="fill" />
            </div>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight mb-3">Password updated</h1>
            <p className="text-sm text-zinc-600 mb-6">Redirecting you to the login page…</p>
            <Link to="/login" className="kr-btn-primary py-2.5 px-6 text-sm inline-block">Go to login now</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <div className="w-12 h-12 bg-orange-600 text-white flex items-center justify-center mb-5">
              <Lock size={24} weight="fill" />
            </div>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight mb-2">Set a new password</h1>
            <p className="text-sm text-zinc-500 mb-6">Choose a strong password with at least 6 characters.</p>

            <label className="block kr-label mb-1.5">New password</label>
            <input
              data-testid="reset-pw1"
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              minLength={6}
              required
              autoFocus
              className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm font-mono focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
            />

            <label className="block kr-label mb-1.5 mt-4">Confirm password</label>
            <input
              data-testid="reset-pw2"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              minLength={6}
              required
              className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm font-mono focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
            />

            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="kr-btn-primary w-full mt-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Lock size={16} weight="bold" />
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
